import React, { useCallback, useMemo } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { TeamsListDataDocument } from "#app/lib/data";
import type { EntityItemConfig, EntityPoolConfig, IconName } from "@salestrak/ui";
import { getEntityGlyph, type EntityData } from "#app/lib/entity-visuals";
import { EntityVisual } from "#app/components/entity";
import { useEntitySearchPool } from "./use-entity-search-pool";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "teams";
const INITIAL_LIMIT = 100;
const SEARCH_LIMIT = 50;
const INITIAL_QUERY_VARS = { limit: INITIAL_LIMIT };

type TeamNode = Omittable<{
  teamId: string;
  teamName: string;
  teamKey?: string | null;
  private?: boolean | null;
  icon?: { icon?: string | null; color?: string | null } | null;
}>;

// THE team → selector-item mapping (padded tinted rounded-square icon, kind
// fallback when no custom icon). Shared by every selector/filter listing teams.
export function teamToPoolItem(node: TeamNode): EntityItemConfig {
  const entity: EntityData = { kind: "team", id: node.teamId, team: node };
  // `render` drives the display; the primitive icon/color stay as data so
  // selections round-trip through selectionToIconEntity (and assignee team
  // detection keeps its avatar.icon fallback).
  const glyph = getEntityGlyph(entity);
  return {
    id: node.teamId,
    title: node.teamName,
    avatar: {
      icon: (glyph.icon ?? undefined) as IconName,
      color: glyph.color ?? undefined,
      padded: true,
      render: () => <EntityVisual entity={entity} size="md" />,
    },
    keywords: node.teamKey ? [node.teamKey] : undefined,
    data: {
      teamKey: node.teamKey,
      private: node.private,
      entityType: "team",
    },
  } satisfies EntityItemConfig;
}

export type UseTeamPoolOptions = Omittable<{
  /** Team IDs to exclude from the pool */
  exclude?: string[];
}>;

/**
 * Fetches workspace teams and returns an EntityPoolConfig for use with EntitySelector.
 */
export function useTeamPool(options?: UseTeamPoolOptions) {
  const { exclude } = options ?? {};
  const client = useApolloClient();

  const { data, loading } = useQuery(TeamsListDataDocument, {
    variables: INITIAL_QUERY_VARS,
  });

  const initialItems = useMemo<EntityItemConfig[]>(() => {
    if (!data?.teams?.edges) return [];
    return data.teams.edges
      .filter((edge) => edge?.node)
      .map((edge) => teamToPoolItem(edge.node));
  }, [data]);

  const runSearch = useCallback(
    async (trimmed: string): Promise<EntityItemConfig[]> => {
      const { data: result } = await client.query({
        query: TeamsListDataDocument,
        variables: { limit: SEARCH_LIMIT, skip: 0, where: { search: trimmed } },
        fetchPolicy: "no-cache",
      });
      if (!result?.teams?.edges) return [];
      return result.teams.edges
        .filter((edge) => edge?.node)
        .map((edge) => teamToPoolItem(edge.node));
    },
    [client],
  );

  const { items, onSearchChange } = useEntitySearchPool({
    baseItems: initialItems,
    runSearch,
    exclude,
    // Stable epoch: the teams query has no filter variants, so cached server-
    // search results never need resetting (and must not be wiped by `User`/team
    // cache re-emission churning the base `data` reference mid-search).
    epoch: INITIAL_QUERY_VARS,
  });

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Teams",
      items,
      onSearchChange,
      // Server-filtered already; client-filtering would hide server matches beyond the first page.
      serverFiltered: true,
    }),
    [items, onSearchChange],
  );

  return { pool, loading };
}

export { POOL_ID as TEAM_POOL_ID };
